import { api } from 'api';
import { Recipe, ThunkCreator } from 'common/hooks/useProducer';
import { getErrorMessage } from 'common/utils/requestErrorHandling';
import { Reducer } from 'redux';
import produce from 'immer';

import exhaustivnessCheck from 'common/utils/exhaustivnessCheck';

type BaconThunker = ThunkCreator<BaconState, BaconActions>;
interface DateWidget {
  type: string;
  label: string;
  urls: {
    no_value: string;
    from_only: string;
    to_only: string;
    both_values: string;
  };
  values: {
    to: string | null;
    from: string | null;
  };
}

export interface ButtonWidget {
  type: string;
  label: string;
  buttons: [
    {
      name: string;
      label: string;
      image_url: null | string;
      url: string;
    },
    {
      name: string;
      label: string;
      image_url: null | string;
      url: string;
    }
  ];
}

/*
Returns the axes (options) this bacon table has so the initial query can be updated
*/

export interface NavResponse {
  axes: { axes: [{ label: string; url: string }]; dimension: string }[];
  values: { label: string; show_url: string }[];
  filters: { drop_url: string; label: string }[];
  widgets?: (DateWidget | ButtonWidget)[];
}

/*
The table data that the bacon API returns. It's very messy, but the concept of bacon is to reduce NxN dimensional data
to 2 dimensions by grouping certain columns.
*/
export interface TableResponse {
  columns: {
    label: string;
    links: {
      drop_axis?: string;
      pivot?: string;
      reset_order?: string;
      hide?: string;
      order?: string;
      order_asc?: string;
    };
    total?: string;
    pivot_value?: string;
    name?: string;
    type: string;
  }[];
  links: { [link: string]: string };
  pages: { current?: boolean; label: string; url: string }[];
  pivots?: {
    label: string;
    links: { drop_axis: string; pivot: string };
    values: { label: string; links: { hide: string; filter: string } };
  }[];
  rows: (string | [string, { filter: string }])[][];
}

/**
 * Actions for retrieving a bacon table
 */
interface BaconStarted {
  type: 'bacon/started';
}

export interface BaconFinished {
  type: 'bacon/finished';
  prevQuery?: null | string;
  nav?: NavResponse;
  table?: TableResponse;
  error?: string;
}
export interface BaconInitialised {
  type: 'bacon/initialised';
  nav?: NavResponse;
  table?: TableResponse;
  error?: string;
}

export type BaconActions = BaconStarted | BaconInitialised | BaconFinished;

/**
 * Stores state of the choices for the bacon table
 */
export interface BaconState {
  pending: boolean;
  prevQuery: string;
  nav: null | NavResponse;
  table: null | TableResponse;
  error?: string;
}

export const defaultBaconState: BaconState = {
  pending: false,
  prevQuery: '',
  nav: null,
  table: null,
  error: null,
};

/**
 * Retrieves the bacon nav header and table contents
 * @endpoint The endpoint to be queried e.g. 'totals'
 * @query The bacon query string
 */
export const doGetBaconData: BaconThunker =
  (endpoint: string, params: { [param: string]: string }, initial = false) =>
  async (dispatch) => {
    dispatch({ type: 'bacon/started' });

    try {
      if (initial) {
        const navResponse = await api.get<NavResponse>(`/${endpoint}/nav`, {
          baseURL: window.urls.reports,
        });
        const tableResponse = await api.get<TableResponse>(
          `/${endpoint}/table`,
          {
            baseURL: window.urls.reports,
          }
        );
        dispatch({
          type: 'bacon/initialised',
          nav: navResponse.data,
          table: tableResponse.data,
        });
      }

      const navResponse = await api.get<NavResponse>(`/${endpoint}/nav`, {
        params: params,
        baseURL: window.urls.reports,
      });
      const tableResponse = await api.get<TableResponse>(`/${endpoint}/table`, {
        params: params,
        baseURL: window.urls.reports,
      });
      dispatch({
        type: 'bacon/finished',
        prevQuery: params.q,
        nav: navResponse.data,
        table: tableResponse.data,
      });
    } catch (e) {
      dispatch({
        type: 'bacon/finished',
        error: getErrorMessage(e.response),
      });
    }
  };

/**
 * The recipe required by useProducer to transform the BaconState
 * This recipe assists in updating any bacon tables
 */
export const baconRecipe: Recipe<BaconState, BaconActions> = (
  draft,
  action
) => {
  switch (action.type) {
    case 'bacon/started':
      draft.pending = true;
      return;

    case 'bacon/initialised':
      draft.pending = true;
      draft.nav = action.nav;
      draft.table = action.table;
      draft.error = action.error;
      return;

    case 'bacon/finished':
      draft.pending = false;
      draft.prevQuery = action.prevQuery;
      action.nav.axes = draft.nav.axes;
      draft.nav = action.nav;
      draft.table = action.table;
      draft.error = action.error;
      return;
  }
};

export interface BaconCacheInstance {
  name: string;
  query: string;
}
interface BaconCacheState {
  [prop: string]: BaconCacheInstance[];
}

const defaultBaconCacheState: BaconCacheState = {
  totals: [
    {
      name: 'Totals Per Upline Agent',
      query:
        'f:receiver:61/a:provider/a:bookie/p:ccy_code/p:stream/hv:turnover/hv:stake',
    },
    {
      name: 'Totals Per Currency',
      query: 'f:receiver:61/a:ccy_code/p:stream/hv:turnover/hv:stake',
    },
    {
      name: 'Upline to Downstream Balance',
      query:
        'f:bookie:ne:molly/a:stream/a:bookie/a:provider/a:receiver/hv:stake/hv:turnover',
    },
  ],
};

interface BaconCacheAdd {
  type: 'bacon/cache/add';
  table: string;
  name: string;
  query: string;
}

interface BaconCacheRemove {
  type: 'bacon/cache/remove';
  table: string;
  instance: BaconCacheInstance;
}

export type BaconCacheActions = BaconCacheAdd | BaconCacheRemove;

export const baconReducer: Reducer<BaconCacheState, BaconCacheActions> = (
  state = defaultBaconCacheState,
  action
) =>
  produce(state, (draft) => {
    switch (action.type) {
      case 'bacon/cache/add':
        if (!draft?.[action.table]) {
          draft[action.table] = [];
        }
        draft[action.table].push({ name: action.name, query: action.query });

        return;

      case 'bacon/cache/remove':
        draft[action.table].splice(
          draft[action.table].indexOf(action.instance, 0),
          1
        );

        return;

      default:
        exhaustivnessCheck(action);
    }
  });
